import * as React from 'react';
import { Button } from './button';
import { SelectField } from './select-field';
import { TextField } from './text-field';
import { useLocalize } from '../components/localize-context';
import { VisitorTypeSelect } from './visitor-type-select';
import { VisitorContext, VisitorType } from './visitor-context';
import { DateField } from './date-field';
import { CloseIcon } from './icon/close-icon';
import { AddIcon } from './icon/add-icon';
import { useAsync } from '../hooks/use-async';
import { CheckIcon } from './icon/check-icon';

export interface CustomerDetailsFormProps {
  initialNumOfAdult?: number;
  initialNumOfKid?: number;
}

export const CustomerDetailsForm = ({
  initialNumOfAdult = 1,
  initialNumOfKid = 0,
}: CustomerDetailsFormProps) => {
  const l = useLocalize();
  const [visitorType] = React.useContext(VisitorContext);
  const isLocal = visitorType === 'local';

  const [formState, dispatch] = React.useReducer(
    formReducer,
    {
      initialNumOfAdult,
      initialNumOfKid,
    },
    init => ({
      records: Array.from({ length: init.initialNumOfAdult })
        .map(_ => defaultRecord)
        .concat(
          Array.from({ length: init.initialNumOfKid }).map(_ => ({
            ...defaultRecord,
            type: 'kid',
          }))
        ),
      mobileNumber: '',
    })
  );

  const submitOperation = useAsync(submitDetails);

  if (submitOperation.isSuccess) {
    return (
      <div role="alert" className="p-4 rounded-md bg-green-100">
        <div className="flex">
          <div className="flex-shrink-0">
            <CheckIcon className="h-5 w-5 text-green-400 fill-current" />
          </div>
          <div className="mo-3">
            <p className="text-sm font-medium leading-5 text-green-700">
              {l('Details submitted successfully')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <form
        onSubmit={ev => {
          ev.preventDefault();
          submitOperation.run({
            customers: formState.records,
            mobileNumber: formState.mobileNumber,
            type: visitorType,
          });
        }}
      >
        <div className="px-4 sm:px-6 py-3 md:flex md:space-x-2 md:items-end">
          <VisitorTypeSelect wrapperClassName="w-36" />
          <TextField
            label={l('Mobile Number')}
            value={formState.mobileNumber}
            onChangeValue={value =>
              dispatch({
                type: 'updateMobile',
                value,
              })
            }
            type="tel"
            autoComplete="tel"
            required
            wrapperClassName="sm:w-52"
          />
        </div>
        <ul>
          {formState.records.map((rec, index) => (
            <li key={index}>
              <div className="relative md:flex md:flex-wrap justify-between items-center px-4 sm:px-6 pt-12 md:pt-2 pb-4 mb-2 bg-white rounded-lg shadow">
                <div className="md:flex-1 md:flex md:space-x-2 md:items-end md:pr-4">
                  <SelectField
                    value={rec.type}
                    onChangeValue={() =>
                      dispatch({
                        type: 'toggleAdultKid',
                        index,
                      })
                    }
                    required
                    options={[
                      {
                        value: 'adult',
                        label: l('Adult'),
                      },
                      {
                        value: 'kid',
                        label: l('Kid'),
                      },
                    ]}
                  />
                  <TextField
                    label={l('Full Name')}
                    value={rec.name}
                    onChangeValue={value =>
                      dispatch({
                        type: 'updateField',
                        payload: {
                          index,
                          value,
                          field: 'name',
                        },
                      })
                    }
                  />
                  {isLocal ? (
                    <TextField
                      label={l('MyKad/MyKid')}
                      value={rec.myKadOrMyKid}
                      onChangeValue={value =>
                        dispatch({
                          type: 'updateField',
                          payload: {
                            index,
                            value,
                            field: 'myKadOrMyKid',
                          },
                        })
                      }
                      required
                      autoComplete="off"
                      wrapperClassName="flex-1"
                    />
                  ) : (
                    <>
                      <TextField
                        label={l('Passport Number')}
                        value={rec.passportNum}
                        onChangeValue={value =>
                          dispatch({
                            type: 'updateField',
                            payload: {
                              value,
                              index,
                              field: 'passportNum',
                            },
                          })
                        }
                        required
                        autoComplete="off"
                        wrapperClassName="flex-1"
                      />
                      <DateField
                        label={l('Date of Birth')}
                        value={rec.dateOfBirth}
                        onChangeValue={value =>
                          dispatch({
                            type: 'updateField',
                            payload: {
                              value,
                              index,
                              field: 'dateOfBirth',
                            },
                          })
                        }
                        allowChangeYear
                        required
                        autoComplete="off"
                        wrapperClassName="flex-1"
                      />
                    </>
                  )}
                </div>
                <div className="absolute top-0 right-0 text-right p-2">
                  <button
                    onClick={() =>
                      dispatch({
                        type: 'removePerson',
                        index,
                      })
                    }
                    className="p-1 rounded-full overflow-hidden focus:outline-none focus:shadow-outline-blue"
                    type="button"
                  >
                    <CloseIcon className="w-7 h-7 text-red-800 fill-current" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="px-4 py-4 sm:px-6">
          <Button
            onClick={() =>
              dispatch({
                type: 'addPerson',
              })
            }
            variant="white"
          >
            <AddIcon />
          </Button>
        </div>
        <div className="px-4 py-4 sm:px-6 text-right">
          <Button
            type="submit"
            variant="primary"
            disabled={submitOperation.isLoading}
          >
            {l('Submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};

const submitDetails = (details: {
  customers: CustomerRecord[];
  mobileNumber: string;
  type: VisitorType;
}) =>
  fetch(`/.netlify/functions/customer`, {
    method: 'POST',
    body: JSON.stringify(details),
  }).then(res => res.json());

interface CustomerRecord {
  type: 'adult' | 'kid';
  name: string;
  passportNum: string;
  myKadOrMyKid: string;
  dateOfBirth: string;
}

interface FormState {
  records: Array<CustomerRecord>;
  mobileNumber: string;
}

type Action =
  | {
      type: 'addPerson';
    }
  | {
      type: 'removePerson';
      index: number;
    }
  | {
      type: 'toggleAdultKid';
      index: number;
    }
  | {
      type: 'updateField';
      payload: {
        field: Exclude<keyof CustomerRecord, 'type'>;
        value: string;
        index: number;
      };
    }
  | {
      type: 'updateMobile';
      value: string;
    };

const defaultRecord: CustomerRecord = {
  type: 'adult',
  name: '',
  passportNum: '',
  myKadOrMyKid: '',
  dateOfBirth: '',
};

const formReducer = (state: FormState, action: Action): FormState => {
  switch (action.type) {
    case 'addPerson':
      return {
        ...state,
        records: state.records.concat(defaultRecord),
      };

    case 'removePerson':
      return {
        ...state,
        records: state.records.filter((_, i) => i !== action.index),
      };

    case 'toggleAdultKid':
      return {
        ...state,
        records: state.records.map((rec, i) =>
          i === action.index
            ? {
                ...rec,
                type: rec.type === 'adult' ? 'kid' : 'adult',
              }
            : rec
        ),
      };

    case 'updateField': {
      const { field, value, index } = action.payload;
      return {
        ...state,
        records: state.records.map((rec, i) =>
          i === index
            ? {
                ...rec,
                [field]: value,
              }
            : rec
        ),
      };
    }

    case 'updateMobile':
      return {
        ...state,
        mobileNumber: action.value,
      };

    default:
      throw new Error(`Not implemented: ${JSON.stringify(action)}`);
  }
};
